/**
 * Frontend types for the new DXF/DWG viewer scene contract.
 *
 * The payload is immutable: mapping and other overlays sit on top of a scene
 * rather than mutating it. `readonly` is the compiler contract for that.
 */

/** Scene schema version the frontend is written against. Bumped by the backend on breaking changes. */
export const DXF_SCENE_SCHEMA_VERSION = 2;

/** A 2D point: `[x, y]`. */
export type DxfPoint = readonly [number, number];

/**
 * A resolved line style — colour, lineweight, and dash pattern. Shared by the
 * entries in the scene's `styles` table and by each layer's default `style`.
 */
export type DxfStyle = {
  /** Hex colour string, e.g. `"#ff0000"`. */
  readonly rgb: string;
  /** Lineweight in millimetres. */
  readonly lineweight: number;
  /**
   * Simplified linetype pattern in drawing units: a positive `[line, gap, line,
   * gap, ...]` sequence (`0` = a dot), empty for a continuous line. The backend
   * resolves this from the file's own linetype table.
   */
  readonly dash: readonly number[];
};

/** A DXF layer and its resolved default style. Layer colour comes from `style.rgb`. */
export type DxfLayer = {
  readonly name: string;
  readonly style: DxfStyle;
};

/**
 * Known source DXF entity type tags on scene primitives. Not exhaustive — ezdxf
 * can emit others — but these are the values the viewer and PMI logic reference.
 */
export const DXF_ENTITY_TYPE = {
  LINE: 'LINE',
  CIRCLE: 'CIRCLE',
  ARC: 'ARC',
  ELLIPSE: 'ELLIPSE',
  SPLINE: 'SPLINE',
  LWPOLYLINE: 'LWPOLYLINE',
  POLYLINE: 'POLYLINE',
  POINT: 'POINT',
  DIMENSION: 'DIMENSION',
  LEADER: 'LEADER',
  MULTILEADER: 'MULTILEADER',
  TOLERANCE: 'TOLERANCE',
  HATCH: 'HATCH',
  INSERT: 'INSERT',
  TEXT: 'TEXT',
  MTEXT: 'MTEXT',
} as const;

/** Source entity types whose geometry is reconstructed dimension annotation (PMI). */
export const DXF_PMI_ENTITY_TYPES: ReadonlySet<string> = new Set([
  DXF_ENTITY_TYPE.DIMENSION,
  DXF_ENTITY_TYPE.LEADER,
  DXF_ENTITY_TYPE.MULTILEADER,
  DXF_ENTITY_TYPE.TOLERANCE,
]);

export type DxfEntity = {
  /** DXF handle, or a synthetic handle for exploded block children. */
  readonly h: string;
  /**
   * Block-definition handle chain for geometry exploded from an INSERT
   * (outermost first). `h` is shared by every primitive of one INSERT, so
   * `[h, ...src]` is the stable unique address of geometry *within* a block
   * instance. Absent for geometry drawn directly in modelspace.
   */
  readonly src?: readonly string[];
  /** Entity type. one of {@link DXF_ENTITY_TYPE} but that enum does not represent an exhasutive set of possible values. */
  readonly t: string;
  /** Index into `DxfScene.layers`. */
  readonly layer: number;
  /** Index into `DxfScene.styles`. */
  readonly style: number;
  /** Polyline vertices, or the exterior ring when `fill` is set (at least one). */
  readonly pts: readonly DxfPoint[];
  /**
   * Indices of the segments (`pts[i]` to `pts[i + 1]`) that are straight in the
   * source geometry, as opposed to facets of a flattened curve. Absent when none
   * are, or when the scene predates the field; either way the row is judged as a
   * whole.
   */
  readonly lines?: readonly number[];
  /**
   * `[cx, cy, r]` of the circle the row is drawn on, when every segment of it is
   * an arc of that one circle: a CIRCLE, an ARC, or a polyline of bulges round
   * one centre. Exact, where a circle fitted from `pts` is off by their
   * rounding. Absent for any other row, or when the scene predates the field.
   * An arc that carries one is still only part of a circle; whether arcs close
   * a circle is decided by how they join.
   */
  readonly circle?: readonly [number, number, number];
  /** `true` when `pts` describes a filled polygon (hatch, glyph fill) rather than a stroke. */
  readonly fill?: boolean;
  /** Inner rings cut out of the fill (glyph counters, hatch islands). */
  readonly holes?: readonly (readonly DxfPoint[])[];
};

/**
 * A raw text string + its oriented bounding box, for the frontend's fitted,
 * selectable text layer. Text is not rendered as glyph geometry; the frontend
 * draws the real string as HTML fitted into this box.
 */
export type DxfText = {
  /** Source text entity handle. */
  readonly h: string;
  /** Block-definition handle chain for text living inside a block (see `DxfEntity.src`). */
  readonly src?: readonly string[];
  /** Plain (formatting-stripped) string, with special chars decoded to Unicode. */
  readonly s: string;
  /** Index into `DxfScene.layers` (so layer toggles hide text too). */
  readonly layer: number;
  /** Resolved text colour (hex), matching the glyph colour it replaces. */
  readonly rgb: string;
  /** WCS bottom-left corner of the oriented text box. */
  readonly pos: DxfPoint;
  /** Box width in the text's local (unrotated) frame, in drawing units. */
  readonly width: number;
  /** Box height in the text's local (unrotated) frame, in drawing units. */
  readonly height: number;
  /** Single-line (cap) height in drawing units, used as the font size. */
  readonly charHeight: number;
  /** DXF horizontal scale (TEXT group 41); 1.0 = unscaled. */
  readonly widthFactor: number;
  /**
   * Alignment as a fraction of the box: `[ax, ay]`, where `(0,0)` is
   * bottom-left, `(0.5,0.5)` center and `(1,1)` top-right.
   */
  readonly anchor: DxfPoint;
  /** Rotation in degrees. */
  readonly rotation: number;
  /** `true` for MTEXT with line breaks / column wrapping (reflowed, not width-fitted). */
  readonly multiline: boolean;
};

/**
 * Where a part file stands in the scene-cache lifecycle.
 *
 * - `ready` — a blob built at `DXF_SCENE_SCHEMA_VERSION` is available.
 * - `building` — no blob yet; the first build is queued or running.
 * - `regenerating` — a blob exists but was built by a different schema version,
 *   so it is withheld while a rebuild runs.
 * - `failed` — no blob can be served. Either the build was attempted for this
 *   schema version and raised, in which case the backend will not retry until
 *   the schema moves on or the file is re-uploaded; or a current blob could not
 *   be presigned.
 * - `unavailable` — this file will never get a scene (not a DXF, infected, or the
 *   viewer is disabled for the group).
 *
 * Only `building` and `regenerating` are transient, and the way to observe them
 * resolving is to watch the `task_status` channel for the build's own report —
 * see `useLiveDxfScene`. Nothing here changes on a timer, so waiting past one of
 * the terminal three never resolves.
 */
export type DxfSceneStatus =
  | 'ready'
  | 'building'
  | 'regenerating'
  | 'failed'
  | 'unavailable';

/**
 * A part file's place in the scene-cache lifecycle.
 *
 * Served both as `PartFile.dxfSceneState` and as the body of
 * `GET /dxf/{partFileUuid}/scene/`, so the two paths narrow identically.
 *
 * Distinct from {@link DxfScene}, which is the geometry payload this state
 * points at once it reaches `ready`.
 *
 * Tagged union on `status`
 */
export type DxfSceneState =
  | {
      readonly status: 'ready';
      /** Presigned URL for the gzipped scene-cache blob. */
      readonly authenticatedUrl: string;
      /** Resolved `$INSUNITS` for measurement formatting. */
      readonly units: number | null;
    }
  | {
      /** A build is needed; the blob, if any, is withheld until it lands. */
      readonly status: 'building' | 'regenerating';
      readonly units: number | null;
    }
  | { readonly status: 'failed' | 'unavailable' };

/**
 * The full viewer scene payload fetched from the presigned scene-cache URL.
 */
export type DxfScene = {
  readonly schemaVersion: number;
  /** Owning `Drawing` PK; `null` for a scene built outside a persisted row. */
  readonly drawingId: number | null;
  /** World-space bounds `[minX, minY, maxX, maxY]`, or `null` for an empty scene. */
  readonly bbox: readonly [number, number, number, number] | null;
  readonly layers: readonly DxfLayer[];
  readonly styles: readonly DxfStyle[];
  readonly entities: readonly DxfEntity[];
  readonly texts: readonly DxfText[];
};

/**
 * Stable mapping key matching backend `entity_key_of`: a hex DXF handle or
 * synthetic `anonN` token, optionally followed by `/src` handle chain.
 */
export const DXF_ENTITY_KEY_PATTERN =
  /^(?:[0-9a-fA-F]+|anon\d+)(?:\/(?:[0-9a-fA-F]+|anon\d+))*$/;

export type DxfEntityKey = string;

export const DXF_MAPPING_ROLE = {
  CUTTING: 'cutting',
  ETCHING: 'etching',
  BENDING: 'bending',
  IGNORE: 'ignore',
} as const;

export type DxfMappingRole =
  (typeof DXF_MAPPING_ROLE)[keyof typeof DXF_MAPPING_ROLE];

export type DxfEntityRoles = readonly DxfMappingRole[];

/** Roles that may occupy the same entity at once (etching + bending). */
export const DXF_CO_APPLICABLE_ROLES: ReadonlySet<DxfMappingRole> = new Set([
  DXF_MAPPING_ROLE.ETCHING,
  DXF_MAPPING_ROLE.BENDING,
]);

/** Only LINE geometry may be assigned a bending role. */
export const DXF_BENDING_ENTITY_TYPE = DXF_ENTITY_TYPE.LINE;

export type DxfSceneMappingParams = {
  partFileUuid?: string | null;
};

/**
 * Mapping overlay for a drawing.
 */
export type DxfSceneMapping = {
  readonly assignments: Readonly<Record<string, DxfEntityRoles>>;
};

export type DxfSceneMappingWritePayload = {
  readonly assignments: Readonly<Record<string, readonly string[]>>;
};

export type DxfSceneMappingWriteVariables = DxfSceneMappingWritePayload & {
  partFileUuid: string;
};

export type DxfMappingValidationCode = string;

/** Validation codes the frontend acts on. Mirrors `dxf.constants.MappingValidationCode`. */
export const DXF_MAPPING_VALIDATION_CODE = {
  /** The mapping draws more than one flat part; it can be split into line items. */
  MULTIPLE_OUTER_CUT_CONTOURS: 'multiple_outer_cut_contours',
} as const;

export type DxfMappingValidationIssue = {
  readonly code: DxfMappingValidationCode;
  readonly entityKeys: readonly DxfEntityKey[];
};

export type DxfMappingValidationResult = {
  readonly issues: readonly DxfMappingValidationIssue[];
  readonly descriptions: Record<DxfMappingValidationCode, string>;
};

/**
 * `POST /dxf/{partFileUuid}/split`. The server saves `assignments` as the
 * mapping, then splits by it. `quoteId` is the quote the viewer expects the new
 * line items to land in; the server refuses (409) if the file no longer resolves
 * to it.
 */
export type DxfSplitVariables = DxfSceneMappingWriteVariables & {
  quoteId: number;
};

/** The `meta` every `dxfSplit` task status message carries. */
export type DxfSplitTaskMeta = {
  /** The quote the line items land in. */
  readonly targetQuoteId: number;
};

/** The `meta` a successful split's final `dxfSplit` task status message carries. */
export type DxfSplitCompletedMeta = DxfSplitTaskMeta & {
  /** The line items created. */
  readonly lineItemCount: number;
};

/**
 * A split task's outcome, from its final `dxfSplit` task status message. A
 * failed split adds no line items, so it has no count.
 */
export type DxfSplitResult =
  | { readonly succeeded: true; readonly meta: DxfSplitCompletedMeta }
  | { readonly succeeded: false; readonly meta: DxfSplitTaskMeta | undefined };
